import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Switch,
  TextField,
  Tooltip,
  Typography,
  InputAdornment,
} from '@mui/material';
import {
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useAdminContext } from '../AdminContext';
import { API_BASE_URL } from '../../../config';
import {
  displayImageSourceForCfbFlairWizard,
  trendsPath,
  matchesFlairTemplateSearch,
} from '../../../utils/subreddit';

interface FlairTemplate {
  id: string;
  class_name: string;
  family_name: string;
  genus_name: string;
  flair_name: string;
  image_name?: string | null;
  text: string;
  abbreviated: string;
  css_class: string;
  flair_code: string;
  image_source: string;
  is_default: boolean;
  user_count?: number;
  class_image?: string | null;
  family_image?: string | null;
  genus_image?: string | null;
  is_award?: boolean;
  has_earned?: boolean;
  percent_change?: number;
  current_count?: number;
}

function compareTrendingFlairs(
  a: { percent_change?: number; current_count?: number; id?: string },
  b: { percent_change?: number; current_count?: number; id?: string }
): number {
  const pa = Number(a.percent_change ?? 0);
  const pb = Number(b.percent_change ?? 0);
  if (Math.abs(pa - pb) > 1e-6) return pb - pa;
  const ca = Number(a.current_count ?? 0);
  const cb = Number(b.current_count ?? 0);
  if (cb !== ca) return cb - ca;
  return String(a.id ?? '').localeCompare(String(b.id ?? ''));
}

function comparePopularFlairs(a: FlairTemplate, b: FlairTemplate): number {
  const ua = Number(a.user_count ?? 0);
  const ub = Number(b.user_count ?? 0);
  if (ub !== ua) return ub - ua;
  const pa = Number(a.percent_change ?? 0);
  const pb = Number(b.percent_change ?? 0);
  if (Math.abs(pa - pb) > 1e-6) return pb - pa;
  return (a.text || '').localeCompare(b.text || '');
}

const POPULAR_TRENDING_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: {
    xs: 'repeat(3, minmax(0, 1fr))',
    sm: 'repeat(4, minmax(0, 1fr))',
    md: 'repeat(4, minmax(0, 1fr))',
    lg: 'repeat(4, minmax(0, 1fr))',
  },
  gap: 0.5,
  width: '100%',
  minWidth: 0,
};

const MAIN_FLAIR_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: {
    xs: 'repeat(3, minmax(0, 1fr))',
    sm: 'repeat(4, minmax(0, 1fr))',
    md: 'repeat(5, minmax(0, 1fr))',
    lg: 'repeat(6, minmax(0, 1fr))',
  },
  gap: 2,
  justifyContent: 'center',
  width: '100%',
  minWidth: 0,
};

const ITEMS_PER_PAGE = 50;

export default function GrantUserFlairTab() {
  const { subreddit, user, canonicalSubredditName, setMessage } = useAdminContext();
  const navigate = useNavigate();

  const [targetUsername, setTargetUsername] = useState('');
  const [flairTemplates, setFlairTemplates] = useState<FlairTemplate[]>([]);
  const [isLoadingFlair, setIsLoadingFlair] = useState(true);
  const [primaryFlair, setPrimaryFlair] = useState<FlairTemplate | null>(null);
  const [secondaryFlair, setSecondaryFlair] = useState<FlairTemplate | null>(null);
  const [activeSlot, setActiveSlot] = useState<'primary' | 'secondary'>('primary');
  const [isGranting, setIsGranting] = useState(false);
  const [preserveFlairText, setPreserveFlairText] = useState(false);
  const [protectedFlairText, setProtectedFlairText] = useState('');
  const [trendingFlairs, setTrendingFlairs] = useState<any[]>([]);
  const [isLoadingTrending, setIsLoadingTrending] = useState(false);

  const [selectedClass, setSelectedClass] = useState<string | null>(null);
  const [selectedFamily, setSelectedFamily] = useState<string | null>(null);
  const [selectedGenus, setSelectedGenus] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [debouncedTargetUsername, setDebouncedTargetUsername] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  /** master_sheet / CDN folder for templates (parent when this sub is a subscriber) */
  const [flairDataSourceSub, setFlairDataSourceSub] = useState<string | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300);
    return () => window.clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    const normalized = targetUsername.trim().replace(/^u\//i, '').toLowerCase();
    const t = window.setTimeout(() => setDebouncedTargetUsername(normalized), 300);
    return () => window.clearTimeout(t);
  }, [targetUsername]);

  const getImageUrl = (imageSource: string, subredditName?: string) => {
    const cdnBaseUrl = import.meta.env.VITE_CDN_BASE_URL || 'https://cdn.redditcfb.com';
    const imagePath = import.meta.env.VITE_CDN_IMAGE_PATH || '60x60';
    const sub = subredditName || flairDataSourceSub || subreddit || '';
    const stem = displayImageSourceForCfbFlairWizard(imageSource);
    return `${cdnBaseUrl}/${imagePath}/${sub}/${stem}.png`;
  };

  const flairTemplatesLoadSeqRef = useRef(0);

  const loadFlairTemplates = async () => {
    if (!subreddit) return;
    const seq = ++flairTemplatesLoadSeqRef.current;
    setIsLoadingFlair(true);
    try {
      // Award flair unlock uses the grant target's eligibility, not the mod's (see award_username on API).
      const awardParam = debouncedTargetUsername
        ? `?award_username=${encodeURIComponent(debouncedTargetUsername)}`
        : '';
      const response = await fetch(`${API_BASE_URL}/flair-templates/${subreddit}${awardParam}`);
      if (response.ok) {
        const data = await response.json();
        if (seq !== flairTemplatesLoadSeqRef.current) return;
        setFlairTemplates(data.templates || []);
        if (typeof data.data_source_sub === 'string' && data.data_source_sub) {
          setFlairDataSourceSub(data.data_source_sub);
        }
      }
    } catch (e) {
      console.error('Error loading flair templates:', e);
      setMessage?.({ type: 'error', text: 'Failed to load flair templates' });
    } finally {
      if (seq === flairTemplatesLoadSeqRef.current) {
        setIsLoadingFlair(false);
      }
    }
  };

  const loadTrendingFlairs = async () => {
    if (!subreddit) return;
    setIsLoadingTrending(true);
    try {
      const response = await fetch(`${API_BASE_URL}/trending-flairs/${subreddit}?limit=12`);
      if (response.ok) {
        const data = await response.json();
        const list = data.trending || [];
        setTrendingFlairs([...list].sort(compareTrendingFlairs));
        if (typeof data.data_source_sub === 'string' && data.data_source_sub) {
          setFlairDataSourceSub(data.data_source_sub);
        }
      }
    } catch (e) {
      console.error('Error loading trending flairs:', e);
    } finally {
      setIsLoadingTrending(false);
    }
  };

  useEffect(() => {
    setFlairDataSourceSub(null);
    loadFlairTemplates();
    loadTrendingFlairs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subreddit, debouncedTargetUsername]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  const classes = useMemo((): Array<{ name: string; image: string | null }> => {
    const out: Array<{ name: string; image: string | null }> = [];
    const seen = new Set<string>();
    for (const t of flairTemplates) {
      if (!seen.has(t.class_name)) {
        seen.add(t.class_name);
        out.push({ name: t.class_name, image: t.class_image || null });
      }
    }
    return out;
  }, [flairTemplates]);

  const families = useMemo((): Array<{ name: string; image: string | null }> => {
    if (!selectedClass) return [];
    const out: Array<{ name: string; image: string | null }> = [];
    const seen = new Set<string>();
    for (const t of flairTemplates) {
      if (t.class_name !== selectedClass) continue;
      if (!seen.has(t.family_name)) {
        seen.add(t.family_name);
        out.push({ name: t.family_name, image: t.family_image || null });
      }
    }
    return out;
  }, [flairTemplates, selectedClass]);

  const genuses = useMemo((): Array<{ name: string; image: string | null }> => {
    const out: Array<{ name: string; image: string | null }> = [];
    const seen = new Set<string>();
    let filteredTemplates = flairTemplates;
    if (selectedClass) {
      filteredTemplates = filteredTemplates.filter((t) => t.class_name === selectedClass);
    }
    if (selectedFamily) {
      filteredTemplates = filteredTemplates.filter((t) => t.family_name === selectedFamily);
    }
    for (const t of filteredTemplates) {
      if (!seen.has(t.genus_name)) {
        seen.add(t.genus_name);
        out.push({ name: t.genus_name, image: t.genus_image || null });
      }
    }
    return out;
  }, [flairTemplates, selectedClass, selectedFamily]);

  useEffect(() => {
    if (selectedClass && families.length === 1 && !selectedFamily) {
      setSelectedFamily(families[0].name);
    }
  }, [selectedClass, families, selectedFamily]);

  const filteredFlairs = useMemo(() => {
    let filtered = flairTemplates;

    if (selectedGenus) {
      filtered = filtered.filter((t) => t.genus_name === selectedGenus);
    } else if (selectedFamily) {
      filtered = filtered.filter((t) => t.family_name === selectedFamily);
      if (selectedClass) {
        filtered = filtered.filter((t) => t.class_name === selectedClass);
      }
    } else if (selectedClass) {
      filtered = filtered.filter((t) => t.class_name === selectedClass);
    }

    if (debouncedSearch) {
      filtered = filtered.filter((t) => matchesFlairTemplateSearch(t, debouncedSearch));
    }

    return filtered;
  }, [flairTemplates, selectedClass, selectedFamily, selectedGenus, debouncedSearch]);

  const paginatedFlairs = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredFlairs.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredFlairs, currentPage]);

  const totalPages = Math.ceil(filteredFlairs.length / ITEMS_PER_PAGE);

  const sameFlairCode = (a: FlairTemplate | null, b: FlairTemplate | null) =>
    Boolean(a && b && a.flair_code.toLowerCase() === b.flair_code.toLowerCase());

  const isFlairBlockedDuplicate = (flair: FlairTemplate) => {
    if (activeSlot === 'primary') {
      return sameFlairCode(secondaryFlair, flair);
    }
    return sameFlairCode(primaryFlair, flair);
  };

  const handleFlairClick = (flair: FlairTemplate) => {
    const isAward = flair.is_award === true;
    const hasEarned = flair.has_earned !== false;
    const isDisabled = isAward && !hasEarned;
    const isDupOther = isFlairBlockedDuplicate(flair);
    if (isDisabled || isDupOther) return;

    if (activeSlot === 'primary' && primaryFlair?.id === flair.id) {
      setPrimaryFlair(null);
    } else if (activeSlot === 'secondary' && secondaryFlair?.id === flair.id) {
      setSecondaryFlair(null);
    } else {
      if (activeSlot === 'primary') {
        if (sameFlairCode(secondaryFlair, flair)) {
          setSecondaryFlair(null);
        }
        setPrimaryFlair(flair);
        setActiveSlot('secondary');
      } else {
        if (sameFlairCode(primaryFlair, flair)) {
          setSecondaryFlair(null);
          setActiveSlot('primary');
          return;
        }
        setSecondaryFlair(flair);
        setActiveSlot('primary');
      }
    }
  };

  const handleNavigationClick = (level: 'class' | 'family' | 'genus', value: string | null) => {
    if (level === 'class') {
      setSelectedClass(value);
      setSelectedFamily(null);
      setSelectedGenus(null);
    } else if (level === 'family') {
      setSelectedFamily(value);
      setSelectedGenus(null);
    } else if (level === 'genus') {
      setSelectedGenus(value);
    }
    setCurrentPage(1);
  };

  const handleGrant = async () => {
    if (!subreddit || !user) return;
    const target = targetUsername.trim().replace(/^u\//i, '').toLowerCase();
    if (!target) {
      setMessage?.({ type: 'warning', text: 'Enter the Reddit username to grant flair to.' });
      return;
    }
    if (!primaryFlair && !secondaryFlair) {
      setMessage?.({ type: 'warning', text: 'Select at least one flair.' });
      return;
    }

    setIsGranting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/grant-user-flair`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.username}`,
        },
        body: JSON.stringify({
          target_username: target,
          primaryFlairId: primaryFlair?.id,
          secondaryFlairId: secondaryFlair?.id,
          protect_text: preserveFlairText,
          flair_text: preserveFlairText ? protectedFlairText : undefined,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setMessage?.({
          type: 'success',
          text: `Granted flair to u/${target}`,
          details: data.flair?.flair_text,
        });
      } else {
        setMessage?.({
          type: 'error',
          text: data.error || 'Failed to grant flair',
          details: data.details,
        });
      }
    } catch (e: any) {
      setMessage?.({ type: 'error', text: 'Failed to grant flair', details: e?.message });
    } finally {
      setIsGranting(false);
    }
  };

  if (!subreddit) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography color="text.secondary">No subreddit context.</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Grant user flair
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Pick flairs the same way users do on the public flair page, then assign them to any Reddit username. This
        updates the database and pushes to Reddit when the sub is authorized.
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'flex-start' }}>
        <TextField
          label="Target Reddit username"
          value={targetUsername}
          onChange={(e) => setTargetUsername(e.target.value)}
          placeholder="without u/"
          size="small"
          sx={{ minWidth: 220 }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={preserveFlairText}
              onChange={(_, v) => setPreserveFlairText(v)}
              color="primary"
            />
          }
          label="Preserve flair text"
        />
      </Box>

      {preserveFlairText && (
        <TextField
          fullWidth
          label="Custom flair text (optional)"
          value={protectedFlairText}
          onChange={(e) => {
            const next = e.target.value;
            const emojis: string[] = [];
            if (primaryFlair?.flair_code) emojis.push(`:${primaryFlair.flair_code}:`);
            if (secondaryFlair?.flair_code) emojis.push(`:${secondaryFlair.flair_code}:`);
            const emojiString = emojis.join(' ');
            const fullText = emojiString + (next ? ` ${next}` : '');
            if (fullText.length <= 64) {
              setProtectedFlairText(next);
            }
          }}
          size="small"
          sx={{ mb: 2 }}
          helperText="Emojis from your selection are prepended; keep total length ≤ 64."
        />
      )}

      {/* Selection summary */}
      <Paper sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
        <Typography variant="subtitle2" gutterBottom>
          Flair slots (click a slot to make it active)
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
          <Box
            onClick={() => setActiveSlot('primary')}
            sx={{
              border: '3px solid',
              borderColor: activeSlot === 'primary' ? 'success.main' : primaryFlair ? 'text.primary' : 'grey.300',
              borderRadius: 2,
              p: 1,
              width: 120,
              height: 140,
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Primary
            </Typography>
            {primaryFlair ? (
              <>
                <Box sx={{ width: 60, height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <img
                    src={getImageUrl(primaryFlair.image_source)}
                    alt={primaryFlair.text}
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  />
                </Box>
                <Typography variant="caption" sx={{ textAlign: 'center', fontSize: '0.65rem' }}>
                  {primaryFlair.text}
                </Typography>
              </>
            ) : (
              <Typography variant="caption" color="text.secondary">
                Empty
              </Typography>
            )}
          </Box>
          <Box
            onClick={() => setActiveSlot('secondary')}
            sx={{
              border: '3px solid',
              borderColor: activeSlot === 'secondary' ? 'success.main' : secondaryFlair ? 'text.primary' : 'grey.300',
              borderRadius: 2,
              p: 1,
              width: 120,
              height: 140,
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Secondary
            </Typography>
            {secondaryFlair ? (
              <>
                <Box sx={{ width: 60, height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <img
                    src={getImageUrl(secondaryFlair.image_source)}
                    alt={secondaryFlair.text}
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  />
                </Box>
                <Typography variant="caption" sx={{ textAlign: 'center', fontSize: '0.65rem' }}>
                  {secondaryFlair.text}
                </Typography>
              </>
            ) : (
              <Typography variant="caption" color="text.secondary">
                Empty
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleGrant}
            disabled={isGranting || (!primaryFlair && !secondaryFlair) || !targetUsername.trim()}
            startIcon={<RefreshIcon />}
          >
            {isGranting ? 'Granting…' : 'Grant flair'}
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setPrimaryFlair(null);
              setSecondaryFlair(null);
            }}
            disabled={!primaryFlair && !secondaryFlair}
            startIcon={<ClearIcon />}
          >
            Clear selection
          </Button>
        </Box>
      </Paper>

      {!isLoadingFlair && flairTemplates.length > 0 && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Paper sx={{ p: 1.5, flex: 1, minWidth: 0, overflow: 'hidden' }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontSize: '0.9rem', mb: 1 }}>
              Popular
            </Typography>
            <Box sx={POPULAR_TRENDING_GRID_SX}>
              {[...flairTemplates]
                .sort(comparePopularFlairs)
                .slice(0, 12)
                .map((flair) => {
                  const isAward = flair.is_award === true;
                  const hasEarned = flair.has_earned !== false;
                  const isDisabled = isAward && !hasEarned;
                  const isDupOther = isFlairBlockedDuplicate(flair);
                  const isClickable = !isDisabled && !isDupOther;

                  const flairBox = (
                    <Box
                      onClick={() => isClickable && handleFlairClick(flair)}
                      sx={{
                        cursor: isClickable ? 'pointer' : 'not-allowed',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 0.5,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        position: 'relative',
                        height: 140,
                        width: '100%',
                        maxWidth: 120,
                        mx: 'auto',
                        opacity: isDisabled || isDupOther ? 0.45 : 1,
                        backgroundColor: isDisabled ? 'action.disabledBackground' : 'transparent',
                        '&:hover': isClickable
                          ? { borderColor: 'primary.main', boxShadow: 1 }
                          : {},
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          fontSize: '0.55rem',
                          color: 'text.secondary',
                          fontWeight: 'bold',
                        }}
                      >
                        {flair.user_count !== undefined
                          ? flair.user_count >= 1000
                            ? `${(flair.user_count / 1000).toFixed(1)}K`.replace('.0', '')
                            : flair.user_count.toString()
                          : '0'}
                      </Typography>
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 0.5,
                          mt: 1,
                        }}
                      >
                        <img
                          src={getImageUrl(flair.image_source)}
                          alt={flair.text}
                          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          textAlign: 'center',
                          fontSize: '0.65rem',
                          lineHeight: 1.2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          minHeight: '2.4em',
                          width: '100%',
                        }}
                      >
                        {flair.text}
                      </Typography>
                    </Box>
                  );

                  return (
                    <Tooltip
                      key={flair.id}
                      title={
                        isDisabled
                          ? 'Award flair — user must earn it'
                          : isDupOther
                            ? 'Same flair_code as the other slot'
                            : ''
                      }
                      arrow
                    >
                      {flairBox}
                    </Tooltip>
                  );
                })}
            </Box>
          </Paper>
          <Paper sx={{ p: 1.5, flex: 1, minWidth: 0, overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontSize: '0.9rem' }}>
                Trending now
              </Typography>
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={() => navigate(trendsPath(canonicalSubredditName || subreddit))}
                sx={{ fontSize: '0.7rem', py: 0.25, px: 1 }}
              >
                View trends
              </Button>
            </Box>
            {isLoadingTrending ? (
              <CircularProgress size={20} />
            ) : trendingFlairs.length > 0 ? (
              <Box sx={POPULAR_TRENDING_GRID_SX}>
                {trendingFlairs.map((flair) => {
                  const flairTemplate = flairTemplates.find((t) => t.id === flair.id) || flair;
                  const isAward = flairTemplate.is_award === true;
                  const hasEarned = flairTemplate.has_earned !== false;
                  const isDisabled = isAward && !hasEarned;
                  const isDupOther = isFlairBlockedDuplicate(flairTemplate);
                  const isClickable = !isDisabled && !isDupOther;

                  const flairBox = (
                    <Box
                      onClick={() => isClickable && handleFlairClick(flairTemplate)}
                      sx={{
                        cursor: isClickable ? 'pointer' : 'not-allowed',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 0.5,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        position: 'relative',
                        height: 140,
                        width: '100%',
                        maxWidth: 120,
                        mx: 'auto',
                        opacity: isDisabled || isDupOther ? 0.45 : 1,
                        backgroundColor: isDisabled ? 'action.disabledBackground' : 'transparent',
                        '&:hover': isClickable
                          ? { borderColor: 'primary.main', boxShadow: 1 }
                          : {},
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          fontSize: '0.55rem',
                          color: flair.percent_change >= 0 ? 'success.main' : 'error.main',
                          fontWeight: 'bold',
                        }}
                      >
                        {flair.percent_change >= 0 ? '+' : ''}
                        {Number(flair.percent_change ?? 0).toFixed(1)}%
                      </Typography>
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 0.5,
                          mt: 1,
                        }}
                      >
                        <img
                          src={getImageUrl(flair.image_source || flairTemplate?.image_source)}
                          alt={flair.text}
                          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          textAlign: 'center',
                          fontSize: '0.65rem',
                          lineHeight: 1.2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          minHeight: '2.4em',
                          width: '100%',
                        }}
                      >
                        {flair.text}
                      </Typography>
                    </Box>
                  );

                  return (
                    <Tooltip
                      key={flair.id}
                      title={
                        isDisabled
                          ? 'Award flair — user must earn it'
                          : isDupOther
                            ? 'Same flair_code as the other slot'
                            : ''
                      }
                      arrow
                    >
                      {flairBox}
                    </Tooltip>
                  );
                })}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                No trending data
              </Typography>
            )}
          </Paper>
        </Box>
      )}

      {!isLoadingFlair && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Search by flair name, abbreviation, image label, or file stem…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            size="small"
          />
        </Paper>
      )}

      {isLoadingFlair ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Accordion
            defaultExpanded
            disableGutters
            elevation={0}
            sx={{
              mb: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">Nested filter</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
                  <Chip
                    label="All"
                    onClick={() => handleNavigationClick('class', null)}
                    sx={{ cursor: 'pointer' }}
                    color={!selectedClass ? 'primary' : 'default'}
                  />
                  {selectedClass && (
                    <>
                      <Chip
                        label={selectedClass}
                        onClick={() => handleNavigationClick('class', selectedClass)}
                        sx={{ cursor: 'pointer' }}
                        color="primary"
                      />
                      {selectedFamily && selectedFamily !== selectedClass && (
                        <Chip
                          label={selectedFamily}
                          onClick={() => handleNavigationClick('family', selectedFamily)}
                          sx={{ cursor: 'pointer' }}
                          color="primary"
                        />
                      )}
                      {selectedGenus && selectedGenus !== selectedFamily && selectedGenus !== selectedClass && (
                        <Chip
                          label={selectedGenus}
                          onClick={() => handleNavigationClick('genus', selectedGenus)}
                          sx={{ cursor: 'pointer' }}
                          color="primary"
                        />
                      )}
                    </>
                  )}
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {!selectedClass && classes.length > 1 && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                        Classes
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {classes.map((classItem) => (
                          <Chip
                            key={classItem.name}
                            label={classItem.name}
                            onClick={() => handleNavigationClick('class', classItem.name)}
                            sx={{ cursor: 'pointer' }}
                            icon={
                              classItem.image ? (
                                <img
                                  src={getImageUrl(classItem.image)}
                                  alt={classItem.name}
                                  style={{ width: 18, height: 18, objectFit: 'contain' }}
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              ) : undefined
                            }
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {selectedClass && !selectedFamily && families.length > 1 && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                        Conferences / families
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {families.map((familyItem) => (
                          <Chip
                            key={familyItem.name}
                            label={familyItem.name}
                            onClick={() => handleNavigationClick('family', familyItem.name)}
                            sx={{ cursor: 'pointer' }}
                            icon={
                              familyItem.image ? (
                                <img
                                  src={getImageUrl(familyItem.image)}
                                  alt={familyItem.name}
                                  style={{ width: 18, height: 18, objectFit: 'contain' }}
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              ) : undefined
                            }
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Paper>
            </AccordionDetails>
          </Accordion>

          {selectedClass && selectedFamily && genuses.length > 0 && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Genus
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {genuses.map((genusItem) => (
                  <Chip
                    key={genusItem.name}
                    label={genusItem.name}
                    onClick={() => handleNavigationClick('genus', genusItem.name)}
                    color={selectedGenus === genusItem.name ? 'primary' : 'default'}
                    sx={{ cursor: 'pointer' }}
                    icon={
                      genusItem.image ? (
                        <img
                          src={getImageUrl(genusItem.image)}
                          alt={genusItem.name}
                          style={{ width: 18, height: 18, objectFit: 'contain' }}
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      ) : undefined
                    }
                  />
                ))}
              </Box>
            </Paper>
          )}

          {filteredFlairs.length === 0 ? (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" gutterBottom>
                  No flairs found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {searchQuery ? 'Try a different search.' : 'Adjust nested filters or search.'}
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <>
              <Box sx={MAIN_FLAIR_GRID_SX}>
                {paginatedFlairs.map((flair) => {
                  const isPrimary = primaryFlair?.id === flair.id;
                  const isSecondary = secondaryFlair?.id === flair.id;
                  const isSelected = isPrimary || isSecondary;
                  const isAward = flair.is_award === true;
                  const hasEarned = flair.has_earned !== false;
                  const isDisabled = isAward && !hasEarned;
                  const isDupOther = isFlairBlockedDuplicate(flair);
                  const isClickable = !isDisabled && !isDupOther;

                  const flairCard = (
                    <Card
                      sx={{
                        cursor: isClickable ? 'pointer' : 'not-allowed',
                        border: isSelected ? '2px solid' : '1px solid',
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        backgroundColor: isSelected
                          ? 'action.selected'
                          : isDisabled
                            ? 'action.disabledBackground'
                            : 'background.paper',
                        opacity: isDisabled || isDupOther ? 0.45 : 1,
                        height: 140,
                        width: '100%',
                        maxWidth: 120,
                        display: 'flex',
                        flexDirection: 'column',
                        mx: 'auto',
                        '&:hover': isClickable
                          ? { borderColor: 'primary.main', boxShadow: 2 }
                          : {},
                      }}
                      onClick={() => isClickable && handleFlairClick(flair)}
                    >
                      <CardContent
                        sx={{ p: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', flexGrow: 1 }}
                      >
                        <Box
                          sx={{
                            width: 60,
                            height: 60,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 0.5,
                          }}
                        >
                          <img
                            src={getImageUrl(flair.image_source)}
                            alt={flair.text}
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{
                            textAlign: 'center',
                            fontSize: '0.65rem',
                            lineHeight: 1.2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            minHeight: '2.4em',
                          }}
                        >
                          {flair.text}
                        </Typography>
                        {isSelected && (
                          <Chip
                            label={isPrimary ? 'Primary' : 'Secondary'}
                            size="small"
                            color="primary"
                            sx={{ mt: 0.5, height: 16, fontSize: '0.6rem' }}
                          />
                        )}
                      </CardContent>
                    </Card>
                  );

                  return (
                    <Tooltip
                      key={flair.id}
                      title={
                        isDisabled
                          ? 'Award flair — user must earn it'
                          : isDupOther
                            ? 'Same flair_code as the other slot'
                            : ''
                      }
                      arrow
                    >
                      {flairCard}
                    </Tooltip>
                  );
                })}
              </Box>

              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <Typography variant="body2">
                    Page {currentPage} of {totalPages} ({filteredFlairs.length} total)
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </Box>
              )}
            </>
          )}
        </>
      )}
    </Paper>
  );
}
